import React from 'react';
import BigNumber from 'bignumber.js';
import { Gauge, DollarSign, Shield, Percent } from 'lucide-react';
import PocketField from './PocketField';
import { getLiquidationBufferInfo, formatR } from '../../utils/liquidationBuffer';
import { PkRow, PkPnl, PkSection, PkHero, PkTwoCol, PkMainField, PkChipRow, PkChip, PkFees, PkFee, PkCols, PkCol, PkExits } from './PocketRows';
import { fmt, fmtLvl, money } from './pocketFormat';

// Stocks market — cosmo layout. No instrument selector; liquidation is the margin
// call; broker/exchange/SEC/FINRA fees live in Settings + the Fees grid (Full).
const PocketStocks = ({ mode = 'compact', hook, onOpenSettings }) => {
  const { values, setValues, metrics } = hook;
  const full = mode === 'full';
  const setField = (field) => (bn) => setValues((prev) => ({ ...prev, [field]: bn }));

  const pl = metrics.priceLevels || {};
  const entry = values.entryPrice;
  const side = (entry instanceof BigNumber) && (values.stopLoss instanceof BigNumber)
    ? (values.stopLoss.gt(entry) ? 'short' : 'long') : null;
  const fb = metrics.feeBreakdown || {};

  const riskIcon = values.riskUnit === 'percent'
    ? <Percent size={12} className="text-[#6b7480]" />
    : <DollarSign size={12} className="text-[#6b7480]" />;
  const bufferInfo = getLiquidationBufferInfo({
    liquidationPrice: pl.liquidationPrice, stopLoss: values.stopLoss, entryPrice: values.entryPrice,
    requestedR: values.safetyBufferR, maxLeverage: values.maxLeverage,
  });
  const bufTone = bufferInfo?.tone === 'danger' ? 'red' : bufferInfo?.tone === 'warn' ? 'amber' : undefined;

  return (
    <div className="pk-body">
      <PkTwoCol>
        <PkMainField label="Entry" copyValue={values.entryPrice}><PocketField value={values.entryPrice} onCommit={setField('entryPrice')} prefix="$" decimals={2} className="text-[12px]" /></PkMainField>
        <PkMainField label="Stop" copyValue={values.stopLoss}><PocketField value={values.stopLoss} onCommit={setField('stopLoss')} prefix="$" decimals={2} className="text-[12px]" /></PkMainField>
      </PkTwoCol>
      <PkChipRow>
        <PkChip icon={<Gauge size={12} className="text-[#6b7480]" />} title="Leverage"><PocketField value={values.maxLeverage} onCommit={setField('maxLeverage')} suffix="x" decimals={0} className="text-[12px]" /></PkChip>
        <PkChip icon={riskIcon} title="Risk"><PocketField value={values.risk} onCommit={setField('risk')} decimals={2} className="text-[12px]" /></PkChip>
        <PkChip label={values.multiTpEnabled ? "TPs" : "R:R"} title={values.multiTpEnabled ? "Multiple take-profits — set in Settings" : "Reward : Risk"}>{values.multiTpEnabled
          ? <span className="pk-mono text-[#eef3f8] text-[12px]">{values.tpCount}</span>
          : <PocketField value={values.nominalRR} onCommit={setField('nominalRR')} suffix="x" decimals={1} className="text-[12px]" />}</PkChip>
        <PkChip icon={<Shield size={12} className="text-[#6b7480]" />} title="Safety buffer"><PocketField value={values.safetyBufferR} onCommit={setField('safetyBufferR')} suffix="R" decimals={2} min={0.25} className="text-[12px]" /></PkChip>
      </PkChipRow>
      <PkChipRow>
        <PkChip label="Fee" title="Total round-turn fee — edit in Settings"><button onClick={onOpenSettings} className="pk-mono text-[#eef3f8] text-[12px] cursor-pointer hover:text-white transition-colors">{fb.total != null && fb.total !== '--' ? `$${fb.total}` : '--'}</button></PkChip>
      </PkChipRow>

      <div className="pk-sep" />

      <PkHero label="Position" side={side} value={money(metrics.positionSize)} sub={`Qty: ${fmt(metrics.quantity, { qty: true })}`} subLeft copyLabel="Position size" />
      <PkRow label="Leverage" value={`${fmt(metrics.leverage, { lev: true })}x`} sub={fmt(metrics.margin, { money: true })} subLeft copyLabel="Leverage" />
      <PkPnl profit={money(values.multiTpEnabled && metrics.multiExit ? metrics.multiExit.totalNetProfit : metrics.effectiveTake)} loss={money(metrics.effectiveStop)} />

      {values.multiTpEnabled && metrics.multiExit && <PkExits data={metrics.multiExit} mode={values.tpMode} pricePrefix="$" />}

      <PkCols>
        <PkCol>
          <PkSection label="Trade" />
          {values.multiTpEnabled && metrics.multiExit ? <>
            <PkRow sm subLeft label="R:R" value={`${fmt(metrics.multiExit.blendedNetRR)}x`} sub="net" copyLabel="Blended net R:R" />
            <PkRow sm label="Avg exit" value={`$${fmtLvl(metrics.multiExit.avgExitPrice)}`} copyLabel="Average exit" />
          </> : <>
            <PkRow sm subLeft label="R:R" value={`${fmt(metrics.optimizedRR)}x`} sub={`${fmt(metrics.nominalRR)}x`} copyLabel="Gross R:R" />
            {full && <PkRow sm subLeft label="Rookie" value={`${fmt(metrics.effectiveRR)}x`} sub={`${fmt(metrics.friction)}R`} copyLabel="Rookie R:R" />}
          </>}
          {full && bufferInfo && <PkRow sm label="Call loss" value={`${formatR(bufferInfo.lossAtLiqR)}R`} tone={bufTone} info={bufferInfo.message} copyLabel="Loss at margin call" />}
        </PkCol>
        <PkCol>
          <PkSection label="Levels" />
          <PkRow sm label="Margin call" value={`$${fmtLvl(pl.liquidationPrice)}`} tone="red" copyLabel="Margin call" />
          <PkRow sm label="B/E" value={`$${fmtLvl(pl.breakEven)}`} copyLabel="Break-even" />
          {!values.multiTpEnabled && <>
            <PkRow sm label="Nom TP" value={`$${fmtLvl(pl.takeProfit)}`} copyLabel="Nominal TP" />
            <PkRow sm label="Opt TP" value={`$${fmtLvl(pl.optimizedTakeProfit)}`} copyLabel="Optimized TP" />
          </>}
        </PkCol>
      </PkCols>

      {full && <>
        <PkSection label="Fees" />
        <PkFees cols={3}>
          <PkFee label="Broker" value={fb.broker ?? '--'} copyLabel="Broker fee" />
          <PkFee label="Exch" value={fb.exchange ?? '--'} copyLabel="Exchange fee" />
          <PkFee label="Clearing" value={fb.clearing ?? '--'} copyLabel="Clearing fee" />
          <PkFee label="SEC" value={fb.sec ?? '--'} copyLabel="SEC fee" />
          <PkFee label="TAF" value={fb.finra ?? '--'} copyLabel="FINRA TAF" />
          <PkFee label="CAT" value={fb.finraCat ?? '--'} copyLabel="FINRA CAT" />
          <PkFee label="Tot TP" value={fmt(metrics.totalFees?.takeProfitFees, { money: true })} copyLabel="Total TP" />
          <PkFee label="Tot SL" value={fmt(metrics.totalFees?.stopLossFees, { money: true })} copyLabel="Total SL" />
        </PkFees>
      </>}
    </div>
  );
};

export default PocketStocks;
